import React, { Component } from "react";
import { FormattedMessage } from "react-intl";
import { connect, type ConnectedProps } from "react-redux";

import { cbctUploadSuccess } from "~/actions/dashboard";
import { addUserActionNotification } from "~/actions/user_notification";
import FileUploader from "~/common/async/FileUploader";
import { FileType, MB } from "~/common/constants";
import { roundFloat } from "~/common/math";
import { fetchCbctUploadLinks, packCbctFiles, validateCbctFiles } from "~/components/patient/patient_shared/cbct-upload";
import { selectClinics } from "~/slices/clinics";
import { selectInstructions } from "~/slices/instructions";
import type { RootState } from "~/store";

import FileInput from "../../FileInput";
import Notification from "../../notification";
import LinksCT from "../addLinks";
import PatientFiles from "../patient_files";

const NOTIFY_ERROR = {
  message: "",
  level: "error",
  position: "tl",
  autoDismiss: "8",
};

const mapStateToProps = (state: RootState) => {
  return {
    patient: state.patient,
    patientClinic: state.patient && state.patient.clinic,
    clinics: selectClinics(state),
    instructions: selectInstructions(state),
    media_s3: state.media_s3,
  };
};

const mapDispatchToProps = (dispatch) => {
  return {
    clearMediaS3: () => dispatch({ type: "media_s3/clear" }),
    cbctUploadSuccess: (files) => dispatch(cbctUploadSuccess(files)),
    addUserActionNotification: (msg) => dispatch(addUserActionNotification(msg)),
  };
};

type PatientUpdateUploadCTProps = PropsFromRedux & {
  showUploadedFiles?: boolean;
  hideHeader?: boolean;
  linkOnly?: boolean;
};

type PatientUpdateUploadCTState = {
  filesCount: number;
  showProgress: boolean;
  uploaderStatus: number;
  error: unknown;
  loadedSize: number;
  totalSize: number;
  progressPerc: string;
};

class PatientUpdateUploadCT extends Component<PatientUpdateUploadCTProps, PatientUpdateUploadCTState> {
  uploader: FileUploader;
  files?: File[];

  constructor(props: PatientUpdateUploadCTProps) {
    super(props);
    this.state = {
      filesCount: 0,
      showProgress: false,
      uploaderStatus: 0,
      error: null,
      loadedSize: 0,
      totalSize: 0,
      progressPerc: "0%",
    };
    this.uploader = new FileUploader();
    this.uploader.on("progress", this.onUploadProgress.bind(this));
    this.uploader.on("finish", this.onUploadSuccess.bind(this));
    this.uploader.on("error", this.onUploadError.bind(this));
    this.inputFiles = this.inputFiles.bind(this);
    this.showModal = this.showModal.bind(this);
  }

  componentDidMount() {
    this.props.clearMediaS3();
  }

  get clinic() {
    if (this.props.instructions && this.props.instructions.clinic_id) {
      if (!this.props.clinics) return;
      return this.props.clinics.find((v) => v.clinic_id === this.props.instructions.clinic_id);
    }

    if (this.props.patientClinic) {
      return this.props.patientClinic;
    }
  }

  async inputFiles(files: File[]) {
    if (!files.length) return;

    const error = validateCbctFiles(files);
    if (error) {
      return this.props.addUserActionNotification({ ...NOTIFY_ERROR, message: error });
    }

    await this.upload(await packCbctFiles(files));
  }

  async upload(files: File[] = []) {
    if (!files.length) return;

    this.setState({ showProgress: true, filesCount: files.length });
    this.lockForm();

    let links;
    try {
      links = await this.getFileLinks(files);
    } catch (error) {
      this.files = files;
      this.onUploadError(error);
      return;
    }

    files.forEach((file) => {
      this.uploader.upload(links[file.name], file);
    });
  }

  async getFileLinks(files: File[] = []) {
    const clinicInfo = this.clinic;
    return await fetchCbctUploadLinks(files, this.props.media_s3.uuid, clinicInfo ? clinicInfo.clinic_id : null);
  }

  onUploadProgress() {
    let { loadedSize, totalSize } = this.uploader;
    loadedSize = roundFloat(loadedSize / MB, 1);
    totalSize = roundFloat(totalSize / MB, 1);
    this.setState({
      loadedSize,
      totalSize,
      progressSizePerc: this.uploader.progressSizePerc,
    });
  }

  onUploadSuccess() {
    this.unlockForm();
    this.setState({ uploaderStatus: this.uploader.promise.status });

    const filesData = this.uploader.fulfilled.map((item) => item.file);
    this.props.cbctUploadSuccess(filesData);
  }

  onUploadError(e: unknown) {
    this.unlockForm();
    this.setState({ uploaderStatus: this.uploader.promise.status, error: e });

    let errorMessage = "E_CONNECTION_FAILED";
    if (e.target && e.target.status) {
      errorMessage = `Network error ${e.target.status}: ${e.target.statusText}`;
    }
    this.props.addUserActionNotification({ ...NOTIFY_ERROR, message: errorMessage });
  }

  lockForm() {
    $("#submit-pacient-btn, #save-pacient-btn").attr("disabled", true);
  }

  unlockForm() {
    $("#submit-pacient-btn, #save-pacient-btn").attr("disabled", false);
  }

  retryUpload(e) {
    e.preventDefault();
    if (this.files) {
      // If this.files exists, then smth wrong with links
      this.upload(this.files);
      this.files = null;
    } else {
      this.uploader.retryAll();
    }

    this.setState({
      uploaderStatus: this.uploader.promise.status,
      error: null,
    });
    this.lockForm();
  }

  showModal() {
    $("#video").modal("show");
  }

  render() {
    return (
      <div className="form-group InstructionsUploadCt">
        {this.props.hideHeader ? null : (
          <>
            <h4>
              <label className="form-label" id="upload-ct" style={{ fontWeight: 900 }}>
                <FormattedMessage id="CBCT_UPLOAD_HEADER" />
              </label>
            </h4>

            <p className="rd-ct-zip-hint">
              <FormattedMessage id="redesign.patient.form.ct.zip_hint" defaultMessage="Archive files (.zip) are accepted" />
            </p>
          </>
        )}

        {this.props.hideHeader ? (
          <p className="tw-mb-2 tw-mt-0 tw-text-sm tw-font-semibold tw-leading-5 tw-text-foreground">
            <FormattedMessage id="patient.files.ct_link_label" />
          </p>
        ) : null}

        <div className="rd-ct-controls">
          {this.props.linkOnly ? null : this.state.showProgress ? this.renderProgress() : this.renderFileInput()}
          <LinksCT />
        </div>

        <Notification />
      </div>
    );
  }

  renderProgress() {
    const { uploaderStatus, filesCount, loadedSize, totalSize, progressSizePerc, error } = this.state;

    const barClassName = [uploaderStatus === 0 ? "progress-bar-primary progress-bar-striped active" : "", uploaderStatus === 1 ? "progress-bar-success" : "", error ? "progress-bar-danger" : ""].join(
      " ",
    );

    let progressTitle = <FormattedMessage id="CBCT_UPLOAD_PROGRESS_TITLE" values={{ filesCount }} />;
    let retryBtn;
    if (uploaderStatus === 1) {
      progressTitle = (
        <span className="text-success">
          <FormattedMessage id="CBCT_UPLOAD_COMPLETE" />
        </span>
      );
    } else if (error || uploaderStatus === 2) {
      progressTitle = (
        <span className="text-danger">
          <FormattedMessage id="CBCT_UPLOAD_ERROR" />
        </span>
      );
      retryBtn = (
        <button className="btn btn-danger" onClick={this.retryUpload.bind(this)}>
          <FormattedMessage id="general.retry" />
        </button>
      );
    }

    return (
      <div className="rd-ct-progress">
        <h4 className="title">{progressTitle}</h4>
        <div className="progress">
          <div className={`progress-bar ${barClassName}`} style={{ width: progressSizePerc }}>
            {loadedSize} MB / {totalSize} MB ({progressSizePerc})
          </div>
        </div>
        {retryBtn}
      </div>
    );
  }

  renderFileInput() {
    const filesUploaded = this.props.patient && this.props.patient.s3_media && this.props.patient.s3_media.length > 0 && this.props.showUploadedFiles;

    return (
      <>
        <div id="ct-media-block">
          <FileInput onInput={this.inputFiles} readFolders={false} hideSelected>
            <FormattedMessage id="UPLOAD_FILE_BTN" />
          </FileInput>
        </div>

        {filesUploaded ? (
          <div className="rd-ct-uploaded">
            <h6 style={{ fontWeight: 900 }}>
              <FormattedMessage id="CBCT_UPLOADED_HEADER" />
            </h6>
            <PatientFiles type={FileType.DICOM} />
          </div>
        ) : null}
      </>
    );
  }
}

const connector = connect(mapStateToProps, mapDispatchToProps);
type PropsFromRedux = ConnectedProps<typeof connector>;
export default connector(PatientUpdateUploadCT);
